import { Link } from "react-router";
import { Layout, PageHeader } from "@/components/layout/Layout";

/**
 * Resources.
 *
 * `legacy/screens/Resources/Resources.js` was a stub — the words "Coming
 * soon..." above a commented-out block of Bulma card demos with Lorem ipsum
 * and `John Smith / @johnsmith` placeholders. Like `Performance.js`, there is
 * nothing to port.
 *
 * Both the navbar and the footer link here, so shipping a second "coming soon"
 * was not an option. What it holds instead is the vocabulary the app itself
 * uses: every term below appears in the UI, and a user who does not know them
 * cannot read their own dashboard. That content is the app's to write and
 * needs no external source.
 *
 * A curated list of outside reading is still worth adding — it is the owner's
 * to pick, and inventing links would be worse than leaving the space.
 */
interface Term {
  term: string;
  short: string;
  body: string;
}

const TERMS: Term[] = [
  {
    term: "Profit target",
    short: "Where you take the win",
    body: "The price at which you have decided to close the position for a gain, chosen before you enter. Reaching it is the only thing Trade-r counts as a win.",
  },
  {
    term: "Stop loss",
    short: "Where you cap the damage",
    body: "The price at which you close for a loss rather than let it grow. The number that decides how much a bad trade is allowed to cost you — which is the part of trading you actually control.",
  },
  {
    term: "Risk to reward",
    short: "What you stand to make against what you stand to lose",
    body: "The distance from your entry to your target, divided by the distance from your entry to your stop. At 3:1 you make three times on a win what you lose on a loss, so you can be wrong more often than right and still come out ahead. Trade-r shows it on every trade.",
  },
  {
    term: "Long and short",
    short: "Which way you expect price to move",
    body: "A long profits when the price rises; a short profits when it falls. A short's targets sit the other way round — profit below the entry, stop above it — and every percentage in Trade-r is signed for direction so both read the same way.",
  },
  {
    term: "Base and quote",
    short: "The two halves of a pair",
    body: "In BTC/USD, Bitcoin is the base — the thing being bought — and USD is the quote, the thing it is priced in. Profit on that trade is denominated in the quote, which is why a portfolio spanning several quotes needs a preferred currency to add up.",
  },
  {
    term: "Preferred currency",
    short: "The one unit everything is restated in",
    body: "Your chosen currency for comparison. Trade-r stores each trade's profit both in its own quote currency and converted to this one, so trades quoted in USD, BTC and EUR can sit in the same total.",
  },
  {
    term: "Realised and unrealised",
    short: "Money made against money on paper",
    body: "A gain is unrealised until you close the position — until then it is a price, not a result. Trade-r's totals and win rate count closed trades only; open ones are shown separately, and never counted as profit.",
  },
];

export function Resources() {
  return (
    <Layout title="Resources">
      <PageHeader
        eyebrow="Knowledge is profit"
        title="Resources"
        subtitle="The vocabulary Trade-r uses, in plain terms"
      />

      <div className="mx-auto max-w-3xl px-6 py-12">
        <p className="max-w-xl text-lg">
          Every term below appears somewhere in the app. None of it is jargon for its own sake —
          if a screen shows you a number, this explains what the number means.
        </p>

        <dl className="mt-12">
          {TERMS.map(({ term, short, body }) => (
            <div key={term} className="border-t-2 border-ink py-8 first:border-t-0 first:pt-0">
              <dt>
                <span className="text-xl font-black tracking-tight">{term}</span>
                <span className="eyebrow mt-1 block">{short}</span>
              </dt>
              <dd className="mt-3 text-sm leading-relaxed">{body}</dd>
            </div>
          ))}
        </dl>

        <div className="field-accent mt-12 border-2 border-ink p-6">
          <h2 className="text-xl font-black tracking-tight">Nothing here is advice</h2>
          <p className="mt-2 text-sm">
            These are definitions, not recommendations. Trade-r does not tell you what to buy,
            when to buy it, or what your targets should be — it holds you to the ones you set.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-3 border-t-2 border-ink pt-8">
          <Link to="/faqs" className="btn-outline btn-sm">
            FAQs
          </Link>
          <Link to="/user/start" className="btn-primary btn-sm">
            Start free
          </Link>
        </div>
      </div>
    </Layout>
  );
}
